import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/ui/Button";
import { Tag } from "primereact/tag";

/**
 * Gen 1 statuses (pending / assigned) are kept so schedules created before B1
 * still render — none exist today on dev or prod, but the map costs nothing and
 * an unmapped status falls through to a grey "info" chip that tells the venue
 * nothing.
 */
const STATUS_SEVERITY = {
    scheduled: "info",
    live:      "success",
    completed: "secondary",
    cancelled: "warning",
    pending:   "info",      // legacy
    assigned:  "success",   // legacy
};

export default function ScheduleCard({ schedule, onDelete }) {
    const { t } = useTranslation();

    // Gen 2 writes the flat columns. fixture_data survives as a back-fill the
    // service builds from them, but it carries only { home_team, away_team,
    // league } — no logos, and the key is `league`, not `league_name`. So the
    // flat columns are the source of truth and fixture_data is the fallback for
    // legacy rows.
    const fd = schedule.fixture_data ?? {};

    const homeTeam  = schedule.home_team      ?? fd.home_team;
    const awayTeam  = schedule.away_team      ?? fd.away_team;
    const league    = schedule.league_name    ?? fd.league_name ?? fd.league;
    const homeLogo  = schedule.home_team_logo ?? fd.home_logo;
    const awayLogo  = schedule.away_team_logo ?? fd.away_logo;
    const leagueLogo = fd.league_logo;

    const sceneName   = schedule.scene?.name;
    const channelName = schedule.channel?.name;

    // A pre-B1 row has no orchestration targets at all — show what it does have.
    const isLegacyTargeting = !sceneName && !channelName
        && (schedule.target_ids?.length ?? 0) > 0;

    // ⚠️ LOUD, NOT SILENT. Channel is the only field that actually routes this
    // match to a device (resolvePlayers() — channel -> receiver -> player).
    // A schedule with neither a channel nor a legacy device/group target
    // "degrades gracefully" server-side — accepted, saved, and shown on
    // NOTHING. That used to be invisible on this card; it no longer is.
    const hasNoDeviceTarget = !channelName && !isLegacyTargeting;

    return (
        <div className="border rounded-xl p-4 bg-white shadow-sm">
            {/* Match header */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                    {leagueLogo && <img src={leagueLogo} alt="" className="w-4 h-4 object-contain" />}
                    <span>{league}</span>
                </div>
                <Tag
                    value={schedule.status}
                    severity={STATUS_SEVERITY[schedule.status] ?? "info"}
                    className="text-xs capitalize"
                />
            </div>

            {/* Teams */}
            <div className="flex items-center justify-between my-2">
                <div className="flex items-center gap-2">
                    {homeLogo && <img src={homeLogo} alt="" className="w-7 h-7 object-contain" />}
                    <span className="font-semibold text-sm text-gray-800">{homeTeam}</span>
                </div>
                <span className="text-xs text-gray-400 font-medium">vs</span>
                <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-gray-800">{awayTeam}</span>
                    {awayLogo && <img src={awayLogo} alt="" className="w-7 h-7 object-contain" />}
                </div>
            </div>

            {/* ⚠️ Loud, not a muted meta line like the rest of this card — this
                is the "silently reaches no device" case made visible. Only for
                schedules that genuinely have no live device target; a legacy
                target_ids row still has one, just via the old mechanism. */}
            {hasNoDeviceTarget && (
                <div className="flex items-center gap-1 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1 mb-2">
                    <i className="pi pi-exclamation-triangle" />
                    <span>{t('match_scheduler.no_channel_warning')}</span>
                </div>
            )}

            {/* Meta */}
            <div className="text-xs text-gray-500 space-y-1 mt-2 mb-3">
                <div className="flex items-center gap-1">
                    <i className="pi pi-clock" />
                    <span>
                        {new Date(schedule.kickoff_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                        {" → "}
                        {new Date(schedule.end_at).toLocaleTimeString([], { timeStyle: "short" })}
                    </span>
                </div>

                {sceneName && (
                    <div className="flex items-center gap-1">
                        <i className="pi pi-sparkles" />
                        <span>{sceneName}</span>
                    </div>
                )}

                {channelName && (
                    <div className="flex items-center gap-1">
                        <i className="pi pi-video" />
                        <span>{channelName}</span>
                    </div>
                )}

                {/* Pre-B1 rows targeted devices/groups directly. */}
                {isLegacyTargeting && (
                    <div className="flex items-center gap-1">
                        <i className={schedule.target_type === "group" ? "pi pi-users" : "pi pi-desktop"} />
                        <span>
                            {t('match_scheduler.n_targets', {
                                count: schedule.target_ids?.length ?? 0,
                                type: schedule.target_type,
                            })}
                        </span>
                    </div>
                )}
            </div>

            {/*
              * Actions — no manual "Trigger now". Gen 2 phases are driven by
              * match:poll; the concept does not translate. POST
              * /match-scheduler/{id}/trigger is deliberately KEPT on the API:
              * the B4 timer fix plans to require API confirmation or a manual
              * trigger before declaring a terminal phase, and deleting the
              * endpoint would leave that with no control to re-surface.
              */}
            <div className="flex gap-2 justify-end">
                <Button
                    icon="pi pi-trash"
                    className="p-button-danger p-button-text p-button-sm"
                    onClick={() => onDelete(schedule.id)}
                    tooltip={t('match_scheduler.remove_tooltip')}
                    tooltipOptions={{ position: "top" }}
                />
            </div>
        </div>
    );
}
